import { useCallback, useEffect, useState } from "react";
import { loadStripe, Stripe, StripeCardNumberElement } from "@stripe/stripe-js";
import { axiosService } from "network/axios";

interface UseBusinessStripePaymentArgs {
  currentStep: number;
  paymentMethod: string;
  userId: string | null;
}

const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE;

export function useBusinessStripePayment({
  currentStep,
  paymentMethod,
  userId,
}: UseBusinessStripePaymentArgs) {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [cardNumberElement, setCardNumberElement] = useState<StripeCardNumberElement | null>(null);
  const [cardBrand, setCardBrand] = useState<string>("");
  const [cardHolderName, setCardHolderName] = useState("");
  const [isSavingCardInfo, setIsSavingCardInfo] = useState(false);
  const [isCardNumberFocused, setIsCardNumberFocused] = useState(false);
  const [isCardExpiryFocused, setIsCardExpiryFocused] = useState(false);
  const [isCardCvcFocused, setIsCardCvcFocused] = useState(false);

  const [cardElements, setCardElements] = useState<{
    cardNumber: any;
    cardExpiry: any;
    cardCvc: any;
  } | null>(null);

  const initStripe = useCallback(async () => {
    if (stripe && cardElements) return;

    const stripeInstance = await loadStripe(STRIPE_PUBLIC_KEY);

    if (!stripeInstance) {
      return;
    }

    setStripe(stripeInstance);
    const elements = stripeInstance.elements();
    
    const elementStyle = {
      base: {
        iconColor: "#000",
        color: "#000",
        fontWeight: 400,
        fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
        fontSize: "16px",
        lineHeight: "30px",
        letterSpacing: "normal",
        fontSmoothing: "antialiased",
        "::placeholder": {
          color: "#9CA3AF",
        },
        ":-webkit-autofill": {
          color: "#777",
        },
      },
      invalid: {
        iconColor: "#F00",
        color: "#F00",
      },
    };

    // Card Number Element
    const cardNumberEl = elements.create("cardNumber", {
      style: elementStyle,
      placeholder: "1234 1234 1234 1234",
    });
    cardNumberEl.mount("#signup-business-card-number-element");
    
    // Card Expiry Element
    const cardExpiryEl = elements.create("cardExpiry", {
      style: elementStyle,
      placeholder: "06/28",
    });
    cardExpiryEl.mount("#signup-business-card-expiry-element");
    
    // Card CVC Element
    const cardCvcEl = elements.create("cardCvc", {
      style: elementStyle,
      placeholder: "369",
    });
    cardCvcEl.mount("#signup-business-card-cvc-element");

    cardNumberEl.on("focus", () => setIsCardNumberFocused(true));
    cardNumberEl.on("blur", () => setIsCardNumberFocused(false));
    cardExpiryEl.on("focus", () => setIsCardExpiryFocused(true));
    cardExpiryEl.on("blur", () => setIsCardExpiryFocused(false));
    cardCvcEl.on("focus", () => setIsCardCvcFocused(true));
    cardCvcEl.on("blur", () => setIsCardCvcFocused(false));

    let isCardNumberComplete = false;
    let isCardExpiryComplete = false;
    let isCardCvcComplete = false;

    cardNumberEl.on("change", (event) => {
      isCardNumberComplete = event.complete;
      if (event.brand) {
        setCardBrand(event.brand);
      }
      if (isCardNumberComplete && isCardExpiryComplete && isCardCvcComplete) {
        setCardNumberElement(cardNumberEl);
      } else {
        setCardNumberElement(null);
      }
    });

    cardExpiryEl.on("change", (event) => {
      isCardExpiryComplete = event.complete;
      if (isCardNumberComplete && isCardExpiryComplete && isCardCvcComplete) {
        setCardNumberElement(cardNumberEl);
      } else {
        setCardNumberElement(null);
      }
    });

    cardCvcEl.on("change", (event) => {
      isCardCvcComplete = event.complete;
      if (isCardNumberComplete && isCardExpiryComplete && isCardCvcComplete) {
        setCardNumberElement(cardNumberEl);
      } else {
        setCardNumberElement(null);
      }
    });

    setCardElements({
      cardNumber: cardNumberEl,
      cardExpiry: cardExpiryEl,
      cardCvc: cardCvcEl,
    });

  }, [stripe, cardElements]);

  useEffect(() => {
    if (currentStep === 4 && paymentMethod === "card") {
      initStripe();
    }

    return () => {
      if (currentStep !== 4 && cardElements) {
        cardElements.cardNumber?.unmount();
        cardElements.cardExpiry?.unmount();
        cardElements.cardCvc?.unmount();
        setCardElements(null);
        setCardNumberElement(null);
        setCardBrand("");
        setIsCardNumberFocused(false);
        setIsCardExpiryFocused(false);
        setIsCardCvcFocused(false);
      }
    };
  }, [currentStep, paymentMethod, initStripe, cardElements]);

  const handleCompleteSetup = useCallback(async () => {
    if (paymentMethod === "card") {
      const hasAnyCardInfo = Boolean(cardHolderName?.trim()) || Boolean(cardNumberElement);

      if (!hasAnyCardInfo) {
        return { success: true };
      }

      if (!stripe || !cardNumberElement || !cardHolderName?.trim()) {
        return { success: false , message: "Please fill in all card details before completing setup." };
      }

      try {
        setIsSavingCardInfo(true);
        const result = await stripe.createPaymentMethod({
          type: "card",
          card: cardNumberElement,
          billing_details: {
            name: cardHolderName,
          },
        });

        const paymentMethodId = result?.paymentMethod?.id;
        if (paymentMethodId && userId) {
          await axiosService.post(`/shipping/put/payment-method/${userId}`, {
            payment_method_id: paymentMethodId,
            is_default: 1,
          });
        }
      } catch (err: any) {

        setIsSavingCardInfo(false);
        return { success: false, message:  err?.data?.messages?.error || "Failed to save your card. Please try again." };
      }

      setIsSavingCardInfo(false);
    }

    return { success: true };
  }, [paymentMethod, stripe, cardNumberElement, cardHolderName, userId]);

  return {
    cardHolderName,
    setCardHolderName,
    cardBrand,
    isCardNumberFocused,
    isCardExpiryFocused,
    isCardCvcFocused,
    isSavingCardInfo,
    handleCompleteSetup,
  };
}
